import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import httpclient from "../../../../Utils";

// Same file-viewer modal used by PublicTracking.js's signature/other-files
// viewer (multi-file nav, copy-to-clipboard, download), rebuilt here as a
// standalone reusable component so this new page doesn't have to duplicate
// PublicTracking's inline dialog state/handlers.
const getFilePath = (file) => {
  if (typeof file === "string") return file;
  return file?.file_path || "";
};

const getFileId = (file) => {
  if (!file || typeof file === "string") return undefined;
  return file.file_id || file.id || file.fileId;
};

const convertBlobToPng = (blob) => {
  return new Promise((resolve, reject) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((pngBlob) => {
        window.URL.revokeObjectURL(objectUrl);
        if (pngBlob) {
          resolve(pngBlob);
        } else {
          reject(new Error("Failed to convert image to PNG"));
        }
      }, "image/png");
    };

    img.onerror = (err) => {
      window.URL.revokeObjectURL(objectUrl);
      reject(err);
    };

    img.src = objectUrl;
  });
};

const FileViewerDialog = ({ open, onClose, files = [], title = "" }) => {
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    setSelectedFileIndex(0);
    onClose?.();
  };

  const handleCloseSnackbar = () => setSnackbar({ open: false, message: "" });

  const handlePrevFile = () => {
    setSelectedFileIndex((prev) => (prev === 0 ? files.length - 1 : prev - 1));
  };

  const handleNextFile = () => {
    setSelectedFileIndex((prev) => (prev === files.length - 1 ? 0 : prev + 1));
  };

  const handleCopyImage = async () => {
    const currentFile = files[selectedFileIndex];
    const fileId = getFileId(currentFile);
    const src = getFilePath(currentFile);
    if (!fileId && !src) return;

    try {
      const response = await httpclient.get(`/view-file/${fileId}`, {
        responseType: "blob",
      });
      const pngBlob = await convertBlobToPng(response.data);
      await navigator.clipboard.write([
        new window.ClipboardItem({ "image/png": pngBlob }),
      ]);
      setSnackbar({ open: true, message: "Image copied to clipboard" });
    } catch (error) {
      try {
        await navigator.clipboard.writeText(src);
        setSnackbar({ open: true, message: "Image link copied to clipboard" });
      } catch (fallbackError) {
        setSnackbar({ open: true, message: "Unable to copy image" });
      }
    }
  };

  const handleDownloadImage = async () => {
    const currentFile = files[selectedFileIndex];
    const fileId = getFileId(currentFile);
    const src = getFilePath(currentFile);
    if (!fileId && !src) return;

    try {
      const response = await httpclient.get(`/download-file/${fileId}`, {
        responseType: "blob",
      });
      const objectUrl = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = currentFile?.file_name || "file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      window.open(src, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.95)",
            borderRadius: "12px",
            overflow: "hidden",
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            px: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "14px", sm: "16px" } }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Copy image">
              <IconButton onClick={handleCopyImage} size="small" sx={{ color: "#fff" }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download image">
              <IconButton onClick={handleDownloadImage} size="small" sx={{ color: "#fff" }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleClose} size="small" sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: { xs: "300px", sm: "420px" },
          }}
        >
          {files.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevFile}
                sx={{
                  position: "absolute",
                  left: { xs: 8, sm: 16 },
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                  zIndex: 1,
                }}
              >
                <ChevronLeftIcon fontSize={isMobile ? "medium" : "large"} />
              </IconButton>

              <IconButton
                onClick={handleNextFile}
                sx={{
                  position: "absolute",
                  right: { xs: 8, sm: 16 },
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                  zIndex: 1,
                }}
              >
                <ChevronRightIcon fontSize={isMobile ? "medium" : "large"} />
              </IconButton>
            </>
          )}

          <Box
            sx={{
              width: "100%",
              px: { xs: 4, sm: 8 },
              py: { xs: 1, sm: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {files.length > 0 && (
              <img
                src={getFilePath(files[selectedFileIndex])}
                alt={files[selectedFileIndex]?.file_name || "Delivery file"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "75vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.75)",
                mt: 2,
                textAlign: "center",
                fontSize: { xs: "11px", sm: "13px" },
                px: 1,
              }}
            >
              {files.length > 0
                ? `${files[selectedFileIndex]?.file_name || "File"} (${selectedFileIndex + 1} of ${files.length})`
                : "No file available"}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FileViewerDialog;
